import { faPen } from '@fortawesome/pro-regular-svg-icons';
import { faPen as faPenSolid } from '@fortawesome/pro-solid-svg-icons';
import {
    ICorporationResponse,
    IIndicatorNotificationSubscriptionResponse,
    ILocationResponse,
    NotificationType,
} from '@kcftech/prometheustypescriptapiclient';
import { HoverIconButton } from 'components/button/HoverIconButton';
import { DataSourceTable } from 'components/data_source_table/DataSourceTable';
import { DataCellRenderInfo, TableColumn } from 'components/data_table/types';
import { ActionMenuTableCell } from 'components/menu/ActionMenuTableCell';
import { NotificationPropertiesCell } from 'components/notification_subscriptions/NotificationProperties';
import {
    NotificationSubscription,
    NotificationSubscriptionsPagedDataSource,
} from 'custom_hooks/data_hooks/data_source/notificationSubscriptionsDataSource';
import { NotificationSubscriptionDeleteButton } from 'pages/Settings/NotificationSubscriptions/IndicatorAlerts/NotificationSubscriptionDeleteButton';
import {
    timespanAsUnacknowledgedPeriodString,
    UnacknowledgedPeriodInfoPopup,
} from 'pages/Settings/NotificationSubscriptions/IndicatorAlerts/shared';
import { NotificationSubscriptionToggleButton } from 'pages/Settings/NotificationSubscriptions/NotificationSubscriptionToggleButton';
import {
    getNotificationTypeDisplayName,
    isIndicatorAlertsSubscription,
} from 'pages/Settings/NotificationSubscriptions/shared';
import { NotificationSubscriptionEditModal } from 'pages/Settings/NotificationSubscriptions/UserNotificationSubscriptionsTable';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useThemeValues } from 'styles/theme';
import { excludeNullAndUndefined } from 'utils/array';
import { getId } from 'utils/data';
import { TimeSpan } from 'utils/TimeSpan';

const SubscriberContainer = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 96rem;
`;

export type NotificationSubscriptionsTableProps = Readonly<{
    location?: ILocationResponse;
    corporation?: ICorporationResponse;
    dataSource: NotificationSubscriptionsPagedDataSource;
    notificationType: NotificationType;
}>;

export function NotificationSubscriptionsGroupDetailsTable({
    dataSource,
    notificationType,
}: NotificationSubscriptionsTableProps) {
    const { reloadPage } = dataSource;
    const { secondaryText, link } = useThemeValues();

    const [
        selectedSubscription,
        setSelectedSubscription,
    ] = useState<NotificationSubscription>();

    const onClose = (saved: boolean) => {
        setSelectedSubscription(undefined);
        if (saved) {
            reloadPage();
        }
    };

    const columns = useMemo<TableColumn<NotificationSubscription>[]>(
        () =>
            excludeNullAndUndefined([
                {
                    header: () => 'Notification Properties',
                    id: 'NotificationProperties',
                    value: NotificationPropertiesCell,
                },
                {
                    header: () => 'Subscriber',
                    id: 'Subscriber',
                    value: ({
                        rowData,
                    }: DataCellRenderInfo<NotificationSubscription>) => (
                        <SubscriberContainer>
                            {rowData.User.UserFirstName} {rowData.User.UserLastName}
                        </SubscriberContainer>
                    ),
                },
                {
                    enabled: notificationType === NotificationType.Indicator,
                    header: () => (
                        <div className="flex-row align-center">
                            Unacknowledged Period
                            <UnacknowledgedPeriodInfoPopup />
                        </div>
                    ),
                    id: 'UnacknowledgedPeriod',
                    value: ({ rowData, ...propsRest }) => {
                        if (isIndicatorAlertsSubscription(rowData)) {
                            return (
                                <UnacknowledgedPeriodCell
                                    rowData={rowData}
                                    {...propsRest}
                                />
                            );
                        }
                        return <></>;
                    },
                },
                {
                    header: () => 'Actions',
                    id: 'Actions',
                    locked: true,
                    value: ({ rowData }) => (
                        <ActionMenuTableCell>
                            <NotificationSubscriptionToggleButton
                                subscription={rowData}
                                onToggle={reloadPage}
                            />
                            <HoverIconButton
                                title={`Edit ${getNotificationTypeDisplayName(
                                    rowData
                                )} notification subscription`}
                                icon={faPen}
                                hoverIcon={faPenSolid}
                                color={secondaryText}
                                hoverColor={link}
                                size="14"
                                onClick={() => setSelectedSubscription(rowData)}
                            />
                            <NotificationSubscriptionDeleteButton
                                subscription={rowData}
                                onDelete={reloadPage}
                            />
                        </ActionMenuTableCell>
                    ),
                },
            ]),
        [notificationType, reloadPage, secondaryText, link]
    );

    return (
        <>
            <DataSourceTable
                dataSource={dataSource}
                rowKey={getId}
                columns={columns}
                noResultMessage="No notification subscriptions found."
            />
            {selectedSubscription && (
                <NotificationSubscriptionEditModal
                    subscription={selectedSubscription}
                    onClose={onClose}
                />
            )}
        </>
    );
}

const UnacknowledgedPeriodCell = ({
    rowData: { UnacknowledgedPeriod },
}: DataCellRenderInfo<IIndicatorNotificationSubscriptionResponse>) => {
    const renderedTime = useMemo(() => {
        const period = TimeSpan.parse(UnacknowledgedPeriod);
        if (period.asMilliseconds() === 0) {
            return '0 (Instant)';
        }
        return timespanAsUnacknowledgedPeriodString(period);
    }, [UnacknowledgedPeriod]);
    return <div className="text-nowrap">{renderedTime}</div>;
};
